import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { HostName, ResourceSegmentInterface, useCloudNavContext } from '@lg-private/cloud-nav';

import { loadEndpoints } from 'baas-ui/endpoints/actions';
import { loadEvents } from 'baas-ui/events/actions';
import { loadFunctions } from 'baas-ui/functions/actions';
import { loadCustomResolvers } from 'baas-ui/graphql/actions';
import { loadApps } from 'baas-ui/home/actions';
import { appServicesVisibleProductTypes } from 'baas-ui/home/types';
import { loadIncomingWebhooks } from 'baas-ui/incomingwebhooks/actions';
import { loadLogForwarders } from 'baas-ui/logs/actions';
import { AsyncDispatch } from 'baas-ui/redux_util';
import { getAppState, getSettingsState } from 'baas-ui/selectors';
import { loadServices } from 'baas-ui/services/actions';
import { isMongoService } from 'baas-ui/services/registry';
import { RootState } from 'baas-ui/types';
import rootUrl from 'baas-ui/urls';
import { loadEnvironmentValues, loadSecrets, loadValues } from 'baas-ui/values/actions';

export enum ResourceType {
  App = 'app',
  CustomResolver = 'custom resolver',
  DataSource = 'data source',
  Endpoint = 'endpoint',
  EnvironmentValue = 'environment value',
  Function = 'function',
  IncomingWebhook = 'incoming webhook',
  LogForwarder = 'log forwarder',
  Secret = 'secret',
  Service = 'service',
  Trigger = 'trigger',
  Value = 'value',
}

const loadAppResources = async ({
  dispatch,
  groupId,
  appId,
}: {
  dispatch: AsyncDispatch;
  groupId: string;
  appId: string;
}) => {
  try {
    const loadedApps = await dispatch(loadApps({ groupId, products: appServicesVisibleProductTypes }));
    if (!loadedApps) {
      return [];
    }

    const resourceSegment: ResourceSegmentInterface = {
      resourceType: ResourceType.App,
      resourceTypeLabel: 'App',
      currentId: appId,
      allResourcesLink: {
        host: HostName.BAAS,
        path: rootUrl.groups().group(groupId).apps().list(),
      },
      availableResources: loadedApps.map((loadedApp) => ({
        resourceType: ResourceType.App,
        resourceId: loadedApp.id,
        resourceName: loadedApp.name,
        parentResourceId: groupId,
        parentResourceType: 'project',
        link: {
          host: HostName.BAAS,
          path: rootUrl.groups().group(loadedApp.groupId).apps().app(loadedApp.id).dashboard(),
        },
      })),
    };

    return [resourceSegment];
  } catch {
    return [];
  }
};

const loadServiceResources = async ({
  dispatch,
  groupId,
  appId,
  svcId,
}: {
  dispatch: AsyncDispatch;
  groupId: string;
  appId: string;
  svcId: string;
}) => {
  const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

  try {
    const [appResources, loadedServices] = await Promise.all([
      loadAppResources({ dispatch, groupId, appId }),
      dispatch(loadServices({ groupId, appId })),
    ]);

    const isDataSourceActive = loadedServices?.some(({ id, type }) => id === svcId && isMongoService(type)) ?? false;

    let resourceType: string;
    let resourceTypeLabel: string;
    let allResourcesPath: string;
    if (isDataSourceActive) {
      resourceType = ResourceType.DataSource;
      resourceTypeLabel = 'Data Source';
      allResourcesPath = appUrl.clusters().list();
    } else {
      resourceType = ResourceType.Service;
      resourceTypeLabel = 'Service';
      allResourcesPath = appUrl.services().list();
    }

    const resourceSegment: ResourceSegmentInterface = {
      resourceType,
      resourceTypeLabel,
      currentId: svcId,
      allResourcesLink: {
        host: HostName.BAAS,
        path: allResourcesPath,
      },
      availableResources:
        loadedServices
          ?.filter(({ type }) => isDataSourceActive === isMongoService(type))
          .map(({ id: resourceId = '', name: resourceName }) => ({
            resourceType,
            resourceId,
            resourceName,
            parentResourceId: appId,
            parentResourceType: ResourceType.App,
            link: {
              host: HostName.BAAS,
              path: isDataSourceActive
                ? appUrl.services().service(resourceId).config().get()
                : appUrl.services().service(resourceId).get(),
            },
          })) ?? [],
    };

    return [...appResources, resourceSegment];
  } catch {
    return [];
  }
};

/**
 * RouteResources returns component wrappers to handle populating the resource nav
 * in CloudNav with the relevant resource path
 */
export const RouteResources = {
  App: ({ children }) => {
    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadAppResources({ dispatch, groupId, appId }));
      }
    }, [groupId, appId]);

    return children;
  },

  Function: ({ children }) => {
    const { funcId } = useParams<{ funcId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadFunctionResources = useCallback(async () => {
      try {
        const [appResources, loadedFunctions] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          dispatch(loadFunctions({ groupId, appId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.Function,
          resourceTypeLabel: 'Function',
          currentId: funcId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.functions().list(),
          },
          availableResources:
            loadedFunctions?.map(({ id: resourceId, name: resourceName }) => ({
              resourceType: ResourceType.Function,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.functions().function(resourceId).get(),
              },
            })) ?? [],
        };
        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, funcId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadFunctionResources());
      }
    }, [loadFunctionResources]);

    return children;
  },

  Trigger: ({ children }) => {
    const { triggerId } = useParams<{ triggerId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadTriggerResources = useCallback(async () => {
      try {
        const [appResources, loadedTriggers] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          dispatch(loadEvents({ groupId, appId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.Trigger,
          resourceTypeLabel: 'Trigger',
          currentId: triggerId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.triggers().list(),
          },
          availableResources:
            loadedTriggers?.map(({ id: resourceId = '', name: resourceName }) => ({
              resourceType: ResourceType.Trigger,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.triggers().trigger(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, triggerId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadTriggerResources());
      }
    }, [loadTriggerResources]);

    return children;
  },

  Value: ({ children }) => {
    const { id: valueId } = useParams<{ id: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadValueResources = useCallback(async () => {
      try {
        const [appResources, loadedValues] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          dispatch(loadValues({ groupId, appId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.Value,
          resourceTypeLabel: 'Value',
          currentId: valueId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.values().list(),
          },
          availableResources:
            loadedValues
              ?.filter(({ fromSecret }) => !fromSecret)
              .map(({ id: resourceId, name: resourceName }) => ({
                resourceType: ResourceType.Value,
                resourceId,
                resourceName,
                parentResourceId: appId,
                parentResourceType: ResourceType.App,
                link: {
                  host: HostName.BAAS,
                  path: appUrl.values().value(resourceId).get(),
                },
              })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, valueId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadValueResources());
      }
    }, [loadValueResources]);

    return children;
  },

  Secret: ({ children }) => {
    const { id: secretId } = useParams<{ id: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadSecretResources = useCallback(async () => {
      try {
        const [appResources, loadedSecrets] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          dispatch(loadSecrets({ groupId, appId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.Secret,
          resourceTypeLabel: 'Secret',
          currentId: secretId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.values().list(),
          },
          availableResources:
            loadedSecrets?.map(({ id: resourceId, name: resourceName }) => ({
              resourceType: ResourceType.Secret,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.secrets().secret(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, secretId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadSecretResources());
      }
    }, [loadSecretResources]);

    return children;
  },

  EnvironmentValue: ({ children }) => {
    const { id: environmentValueId } = useParams<{ id: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadEnvironmentValueResources = useCallback(async () => {
      try {
        const [appResources, loadedEnvironmentValues] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          dispatch(loadEnvironmentValues({ groupId, appId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.EnvironmentValue,
          resourceTypeLabel: 'Environment Value',
          currentId: environmentValueId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.values().list(),
          },
          availableResources:
            loadedEnvironmentValues?.map(({ id: resourceId, name: resourceName }) => ({
              resourceType: ResourceType.EnvironmentValue,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.environmentValues().environmentValue(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, environmentValueId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadEnvironmentValueResources());
      }
    }, [loadEnvironmentValueResources]);

    return children;
  },

  Service: ({ children }) => {
    const { svcId } = useParams<{ svcId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadServiceResources({ dispatch, groupId, appId, svcId }));
      }
    }, [loadServiceResources, svcId]);

    return children;
  },

  IncomingWebhook: ({ children, svcId }: { children: JSX.Element; svcId: string }) => {
    const { incomingWebhookId } = useParams<{ incomingWebhookId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadIncomingWebhookResources = useCallback(async () => {
      try {
        const [appResources, svcResources, loadedIncomingWebhooks] = await Promise.all([
          loadAppResources({ dispatch, groupId, appId }),
          loadServiceResources({ dispatch, groupId, appId, svcId }),
          dispatch(loadIncomingWebhooks({ groupId, appId, svcId })),
        ]);

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.IncomingWebhook,
          resourceTypeLabel: 'Incoming Webhook',
          currentId: incomingWebhookId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.services().service(svcId).incomingWebhooks().list(),
          },
          availableResources:
            loadedIncomingWebhooks?.map(({ id: resourceId, name: resourceName }) => ({
              resourceType: ResourceType.IncomingWebhook,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.services().service(svcId).incomingWebhooks().incomingWebhook(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, ...svcResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, svcId, incomingWebhookId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadIncomingWebhookResources());
      }
    }, [loadIncomingWebhookResources]);

    return children;
  },

  LogForwarder: ({ children }) => {
    const { logForwarderId } = useParams<{ logForwarderId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadLogForwarderResources = useCallback(async () => {
      try {
        const appResources = await loadAppResources({ dispatch, groupId, appId });
        const loadedLogForwarders = await dispatch(loadLogForwarders({ groupId, appId }));

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.LogForwarder,
          resourceTypeLabel: 'Log Forwarder',
          currentId: logForwarderId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.logs().forwarders().list(),
          },
          availableResources:
            loadedLogForwarders?.map(({ id: resourceId, name: resourceName }) => ({
              resourceType: ResourceType.LogForwarder,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.logs().forwarders().forwarder(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, logForwarderId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadLogForwarderResources());
      }
    }, [loadLogForwarderResources]);

    return children;
  },

  Endpoint: ({ children }) => {
    const { endpointId } = useParams<{ endpointId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadEndpointResources = useCallback(async () => {
      try {
        const appResources = await loadAppResources({ dispatch, groupId, appId });
        const loadedEndpoints = await dispatch(loadEndpoints({ groupId, appId }));

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.Endpoint,
          resourceTypeLabel: 'Endpoint',
          currentId: endpointId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.endpoints().list(),
          },
          availableResources:
            loadedEndpoints?.map(({ id: resourceId = '', route: resourceName }) => ({
              resourceType: ResourceType.Endpoint,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.endpoints().endpoint(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, endpointId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadEndpointResources());
      }
    }, [loadEndpointResources]);

    return children;
  },

  CustomResolver: ({ children }) => {
    const { customResolverId } = useParams<{ customResolverId: string }>();

    const { setProjectResourcePath } = useCloudNavContext();
    const { showCloudNav } = useSelector((state: RootState) => getSettingsState(state));
    const dispatch = useDispatch<AsyncDispatch>();

    const app = useSelector((state: RootState) => getAppState(state).app);
    const groupId = app?.groupId ?? '';
    const appId = app?.id ?? '';
    const appUrl = rootUrl.groups().group(groupId).apps().app(appId);

    const loadCustomResolverResources = useCallback(async () => {
      try {
        const appResources = await loadAppResources({ dispatch, groupId, appId });
        const loadedCustomResolvers = await dispatch(loadCustomResolvers({ groupId, appId }));

        const resourceSegment: ResourceSegmentInterface = {
          resourceType: ResourceType.CustomResolver,
          resourceTypeLabel: 'Custom Resolver',
          currentId: customResolverId,
          allResourcesLink: {
            host: HostName.BAAS,
            path: appUrl.graphql().customResolvers().list(),
          },
          availableResources:
            loadedCustomResolvers?.map(({ id: resourceId = '', fieldName: resourceName }) => ({
              resourceType: ResourceType.CustomResolver,
              resourceId,
              resourceName,
              parentResourceId: appId,
              parentResourceType: ResourceType.App,
              link: {
                host: HostName.BAAS,
                path: appUrl.graphql().customResolvers().customResolver(resourceId).get(),
              },
            })) ?? [],
        };

        return [...appResources, resourceSegment];
      } catch {
        return [];
      }
    }, [groupId, appId, customResolverId]);

    useEffect(() => {
      if (showCloudNav) {
        setProjectResourcePath(loadCustomResolverResources());
      }
    }, [loadCustomResolverResources]);

    return children;
  },
};
